import React from 'react';
import { Form, Input } from 'antd';
import { Translate, useTranslate } from '@above-the-treeline/localization';
import { RegistrationTypes } from '../RegistrationEnums';
import delay from '../../../../../util/delay';
import { useUser } from '../../../../User/containers/UserDataStore';
import RegistrationFormSubmitButton from './RegistrationFormSubmitButton/RegistrationFormSubmitButton';
import { FormComponentProps } from 'antd/lib/form/Form';
import { RegistrationFormItems } from './RegistrationFormItems';
import ForgotPasswordSuccessMessage from './ForgotPasswordSuccessMessage/ForgotPasswordSuccessMessage';
import { IFormItem } from './RegistrationFormTypes';
import SignUpForm from './SignUpForm/SignUpForm';
import LogInForm from './LogInForm/LogInForm';
import ForgotPasswordForm from './ForgotPasswordForm/ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm/ResetPasswordForm';
import ResetPasswordSuccessMessage from './ResetPasswordSuccessMessage/ResetPasswordSuccessMessage';
import './registrationForm.less';
import { useAccountOrMembershipRequiredModalContext } from '../../../AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';
import LoadingSpinner, {
	LoadingSpinnerSize,
} from '../../../../UI/LoadingSpinner';
import { Select, Checkbox } from 'antd';
import { useRegistration } from '../../../../Registration/containers/RegistrationDataStore';
const { Option } = Select;

const { Item } = Form;

interface IRegistrationFormProps extends FormComponentProps {
	type: string;
	isLoading: boolean;
	setIsLoading: (isLoading: boolean) => void;
	userToken?: string;
}

type FieldsValue = {
	email?: string;
	firstName?: string;
	lastName?: string;
	pword?: string;
	recaptcha?: string;
	confirm_pword?: string;
	current_pword?: string;
	country_code?: string;
	do_keep_me_updated?: boolean;
};

function RegistrationForm({
	form,
	type,
	isLoading,
	setIsLoading,
	userToken,
}: IRegistrationFormProps): JSX.Element {
	const { translate } = useTranslate();
	const [error, setError] = React.useState<JSX.Element | null>(null);
	const [
		showKeepMeUpdatedCheckbox,
		setShowKeepMeUpdatedCheckbox,
	] = React.useState<boolean>(false);
	const [
		successMessage,
		setSuccessMessage,
	] = React.useState<JSX.Element | null>(null);
	const {
		getFieldValue,
		getFieldDecorator,
		validateFields,
		resetFields,
	} = form;
	const isSignUpModal = type === RegistrationTypes.SignUp;
	const isResetPasswordModal = type === RegistrationTypes.ResetPassword;
	const {
		logInUser,
		signUpUser,
		sendForgotPasswordEmail,
		changeUserPassword,
	} = useUser();

	const {
		setIsVisible,
		pathForRedirect,
	} = useAccountOrMembershipRequiredModalContext();

	React.useEffect(() => {
		setSuccessMessage(null);
		resetFields();
	}, [type]);

	const handleError = err => {
		setError(
			err.message ? (
				<>
					<Translate id="there_was_an_error_with_your_request" />
					{err.message}. <Translate id="please_try_again" />
				</>
			) : (
				<div className="registration-error-text">{err}</div>
			),
		);
	};

	const handleSuccess = () => {
		const message = isResetPasswordModal ? (
			<ResetPasswordSuccessMessage />
		) : (
			<ForgotPasswordSuccessMessage />
		);
		setSuccessMessage(message);
	};

	const handleStopLoading = async () => {
		await delay(500);
		setIsLoading(false);
	};

	const getSignUpValues = (fieldsValue: FieldsValue) => {
		return {
			user: {
				firstName: fieldsValue.firstName,
				lastName: fieldsValue.lastName,
				email: fieldsValue.email,
				password: fieldsValue.pword,
			},
			recaptchaResponse: fieldsValue.recaptcha,
			doSendEmail: true,
			countryCode: fieldsValue.country_code,
			doKeepMeUpdated: fieldsValue.do_keep_me_updated,
		};
	};

	const getChangePasswordValues = (fieldsValue: FieldsValue) => {
		return {
			password: fieldsValue.pword,
			verifyPassword: fieldsValue.confirm_pword,
			token: userToken,
			currentPassword: fieldsValue.current_pword,
		};
	};

	const onSuccess = () => {
		setIsVisible(false);
		pathForRedirect
			? (window.location.href = pathForRedirect)
			: window.location.reload();
	};

	const performRegistrationAction = (fieldsValue: FieldsValue) => {
		switch (type) {
			case RegistrationTypes.SignUp:
				return signUpUser(
					getSignUpValues(fieldsValue),
					handleError,
					onSuccess,
				);
			case RegistrationTypes.LogIn:
				return logInUser(fieldsValue, handleError, onSuccess);
			case RegistrationTypes.ForgotPassword:
				return sendForgotPasswordEmail(
					fieldsValue,
					handleError,
					handleSuccess,
				);
			case RegistrationTypes.ResetPassword:
				return changeUserPassword(
					getChangePasswordValues(fieldsValue),
					handleError,
					handleSuccess,
				);
		}
	};

	const handleRegistration = e => {
		e.preventDefault();
		validateFields((err, fieldsValue) => {
			if (err) return;
			setError(null);
			setIsLoading(true);
			return performRegistrationAction(fieldsValue).then(
				handleStopLoading,
			);
		});
	};

	const getPasswordRules = () => {
		const { rules } = RegistrationFormItems.Password;
		const atLeastOneUppercase = '?=.*?[A-Z]';
		const atLeastOneLowercase = '?=.*?[a-z]';
		const atLeastOneNumber = '?=.*?[0-9]';
		const atLeastOneSpecialCharacter = '?=.*?[#?!@$%^&*-]';
		const minimumLength = 8;
		const passwordCharacterRequirement = new RegExp(
			`^(${atLeastOneUppercase})(${atLeastOneLowercase})(${atLeastOneNumber})(${atLeastOneSpecialCharacter}).{${minimumLength},}$`,
		);
		return isSignUpModal || isResetPasswordModal
			? [
					...RegistrationFormItems.Password.rules,
					{
						pattern: passwordCharacterRequirement,
						message: <Translate id="password_must_be_at_least" />,
					},
			  ]
			: rules;
	};

	const getConfirmPasswordRules = () => {
		const { rules } = RegistrationFormItems.ConfirmPassword;
		return [
			...rules,
			{
				validator: (_, value) => {
					if (!value || getFieldValue('pword') === value) {
						return Promise.resolve();
					}
					return Promise.reject(
						new Error(`${translate('passwords_do_not_match')}`),
					);
				},
			},
		];
	};

	const getItemRules = (item: IFormItem) => {
		const { label, rules } = item;
		switch (label) {
			case RegistrationFormItems.ConfirmPassword.label:
				return getConfirmPasswordRules();
			case RegistrationFormItems.Password.label:
				return getPasswordRules();
			default:
				return rules;
		}
	};

	const getFormItem = (
		item: IFormItem,
		substituteLabel?: JSX.Element,
		data?: any,
	) => {
		const { label, fieldDecorator } = item;

		const itemVisibility =
			label === RegistrationFormItems.KeepMeUpdated.label &&
			showKeepMeUpdatedCheckbox === false
				? false
				: true;

		return (
			<Item
				style={{
					visibility: itemVisibility ? 'visible' : 'hidden',
				}}
				label={substituteLabel || label}>
				{getFieldDecorator(fieldDecorator, {
					rules: getItemRules(item),
				})(getFieldInput(item, data))}
			</Item>
		);
	};

	function handleSelectChange(countryCodeValue: string) {
		const nonGdprCountryCodes = ['us', 'ca', 'au'];

		if (nonGdprCountryCodes.indexOf(countryCodeValue) === -1) {
			setShowKeepMeUpdatedCheckbox(true);
		} else {
			setShowKeepMeUpdatedCheckbox(false);
		}
	}

	const getFieldInput = (item: IFormItem, data?: any) => {
		const { label, fieldType } = item;
		const isPasswordItem = label === RegistrationFormItems.Password.label;
		const isConfirmPasswordItem =
			label === RegistrationFormItems.ConfirmPassword.label;
		const isCurrentPasswordItem =
			label === RegistrationFormItems.CurrentPassword.label;
		const isSelectFieldType = fieldType === 'select';
		const isCheckboxFieldType = fieldType === 'checkbox';

		if (isPasswordItem || isConfirmPasswordItem || isCurrentPasswordItem) {
			return <Input.Password />;
		} else if (isCheckboxFieldType) {
			return <Checkbox></Checkbox>;
		} else if (isSelectFieldType) {
			return (
				<Select onChange={handleSelectChange}>
					{data.map(element => (
						<Option key={element['id']}>
							{element['description']}
						</Option>
					))}
				</Select>
			);
		} else {
			return <Input></Input>;
		}
	};

	const getFormFields = () => {
		switch (type) {
			case RegistrationTypes.SignUp:
				return <SignUpForm getFormItem={getFormItem} />;
			case RegistrationTypes.LogIn:
				return <LogInForm getFormItem={getFormItem} />;
			case RegistrationTypes.ForgotPassword:
				return <ForgotPasswordForm getFormItem={getFormItem} />;
			case RegistrationTypes.ResetPassword:
				return <ResetPasswordForm getFormItem={getFormItem} />;
			default:
				throw new Error(`Invalid type: ${type}`);
		}
	};

	const registrationDataStore = useRegistration();
	return isLoading ? (
		<LoadingSpinner size={LoadingSpinnerSize.small} />
	) : successMessage ? (
		successMessage
	) : (
		<>
			{error && <div className="registration-error">{error}</div>}
			<Form
				className="registration-modal-form"
				onSubmit={handleRegistration}
				hideRequiredMark={!isSignUpModal}>
				{registrationDataStore && getFormFields()}
				<RegistrationFormSubmitButton type={type} form={form} />
			</Form>
		</>
	);
}

const WrappedRegistrationForm = Form.create<IRegistrationFormProps>({
	name: 'registration',
})(RegistrationForm);

export default WrappedRegistrationForm;
